import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  ButtonGroup,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import axios from "axios";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteMeeshoOrderManagementOrder,
  getMeeshoOrderManagementOrderList,
} from "app/redux/slice/meeshoOrderManagementSlice";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { userType } from "app/helpers/userType";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const OrderList = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();
  const meeshoData = useSelector((state) => state?.meeshoOrderManagement);
  const orders = meeshoData?.orders?.list || [];

  // ACTIVE state used for fetch
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    days: "",
    sku: "",
    paymentStatus: "",
  });
  const [dateRange, setDateRange] = useState([null, null]);

  // TEMP state used for inputs until user clicks Apply
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilters, setTempFilters] = useState({
    status: "",
    days: "",
    sku: "",
    paymentStatus: "",
  });
  const [tempDateRange, setTempDateRange] = useState([null, null]);

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "meesho-management-orders" });

  const handleTempDateChange = (newDateRange) => {
    setTempDateRange(newDateRange);
  };

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();

  const statusOptions = [
    { value: "DELIVERED", label: "Delivered", color: "success" },
    { value: "CANCELLED", label: "Cancelled", color: "error" },
    { value: "SHIPPED", label: "Shipped", color: "info" },
    { value: "RTO_LOCKED", label: "RTO Locked", color: "warning" },
    { value: "RTO_OFD", label: "RTO OFD", color: "warning" },
    { value: "RTO_COMPLETE", label: "RTO Complete", color: "info" },
    { value: "CUSTOMER_RETURN", label: "Customer Return", color: "info" },
    { value: "CURIOR_RETURN", label: "Curior Return", color: "info" },
    { value: "READY_TO_SHIP", label: "Ready to Ship", color: "primary" },
    { value: "HOLD", label: "Hold", color: "default" },
    { value: "CLAIM_PENDING", label: "Claim Pending", color: "warning" },
    { value: "CLAIM_RESOLVED", label: "Claim Resolved", color: "success" },
    { value: "CLAIM_REJECTED", label: "Claim Rejected", color: "error" },
    {
      value: "DOOR_STEP_EXCHANGED",
      label: "Door Step Exchanged",
      color: "info",
    },
    { value: "PENDING", label: "Pending", color: "info" },
    { value: "LOST", label: "Lost", color: "error" },
  ];

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );
  const [permission, setPermission] = useState({
    deletePermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        deletePermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "delete-order") {
            permissionValue.deletePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  // fetch when active params change
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    dispatch,
    searchTerm,
    order,
    orderBy,
    filters,
    dateRange,
  ]);

  const fetchOrders = async () => {
    const params = {
      page: page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
      ...filters,
    };

    // attach date range only when both are present
    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }

    dispatch(getMeeshoOrderManagementOrderList(params));
  };

  const columns = [
    {
      id: "subOrderNo",
      label: "Sub Order No",
      sort: false,
    },
    {
      id: "orderDate",
      label: "Date",
      sort: false,
      render: (row) => <>{moment.utc(row?.orderDate).format("DD/MM/YYYY")}</>,
    },
    {
      id: "customerState",
      label: "Customer State",
      sort: false,
    },
    {
      id: "productName",
      label: "Product",
      sort: false,
      render: (row) => (
        <Typography variant="body1" noWrap sx={{ maxWidth: 200 }}>
          {row?.productName}
        </Typography>
      ),
    },
    {
      id: "sku",
      label: "SKU",
      sort: false,
    },
    {
      id: "paymentStatus",
      label: "Payment Status",
      sort: false,
      render: (row) => {
        const live = row?.paymentLiveStatus;
        if (live === "return")
          return <Chip label="Return" color="warning" size="small" />;
        if (live === "delivered")
          return <Chip label="Paid" color="success" size="small" />;
        if (row?.paymentStatus === "PENDING")
          return <Chip label="Pending" color="default" size="small" />;
        return <span>-</span>;
      },
    },
    {
      id: "status",
      label: "Status",
      sort: false,
      render: (row) => <>{getStatusChip(row?.status)}</>,
    },
    {
      id: "size",
      label: "Size",
      sort: false,
    },
    {
      id: "quantity",
      label: "Quantity",
      sort: false,
    },
    {
      id: "supplierDiscountedPrice",
      label: "Price",
      sort: false,
      isTotal: true,
      format: (value) => formatCurrency(value),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedOrder(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  // TEMP input handlers (do not trigger fetch)
  const handleTempSearchChange = (event) => {
    setTempSearchTerm(event.target.value);
  };

  const handleTempFilterChange = (field, value) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  // apply temporary inputs to active state + reset page
  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setFilters({ ...tempFilters });
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  // clear both temp and active filters
  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");

    const cleared = { status: "", days: "", sku: "", paymentStatus: "" };
    setTempFilters(cleared);
    setFilters(cleared);

    setTempDateRange([null, null]);
    setDateRange([null, null]);

    handleChangePage(0, 0);
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedOrders.length === 0) {
      enqueueSnackbar("Please select orders to update", { variant: "warning" });
      return;
    }

    try {
      const response = await axios.put("/meesho/orders/bulk-status", {
        orderIds: selectedOrders,
        status: newStatus,
        reason: statusReason,
      });

      if (response.data.statusCode === 200) {
        enqueueSnackbar(
          `Updated ${response.data.data.updated} orders successfully`,
          { variant: "success" },
        );
        setSelectedOrders([]);
        setStatusDialog(false);
        fetchOrders();
      }
    } catch (error) {
      console.error("Bulk update error:", error);
      enqueueSnackbar("Failed to update orders", { variant: "error" });
    }
  };

  const handleDeleteOrder = async () => {
    dispatch(deleteMeeshoOrderManagementOrder(deleteActionProps))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res?.data?.responseMessage, {
            variant: "success",
          });
          fetchOrders();
          closeDelete();
        } else {
          enqueueSnackbar(res?.data?.responseMessage, {
            variant: "error",
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getStatusChip = (status) => {
    const statusConfig = statusOptions.find(
      (option) => option.value === status,
    );
    return (
      <Chip
        label={statusConfig?.label || status}
        color={statusConfig?.color || "default"}
        size="small"
      />
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Orders List
      </Typography>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3.75}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search orders ..."
                value={tempSearchTerm}
                onChange={handleTempSearchChange}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date - To Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={handleTempDateChange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>{" "}
            <Grid item xs={12} sm={3.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ButtonGroup size="small" variant="outlined">
                  {QUICK_RANGES?.map((r) => (
                    <Button
                      key={r.key}
                      onClick={() => {
                        // set quick ranges into TEMP so user can inspect before apply
                        setTempDateRange(r.getRange());
                      }}
                      disabled={meeshoData?.statusList?.loading}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} md={2.25}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={tempFilters?.status}
                  onChange={(e) =>
                    handleTempFilterChange("status", e?.target?.value)
                  }
                  label="Status"
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option?.value} value={option?.value}>
                      {option?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Old Orders</InputLabel>
                <Select
                  value={tempFilters?.days}
                  onChange={(e) =>
                    handleTempFilterChange("days", e?.target?.value)
                  }
                  label="Old Orders"
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="30">30-Day Old</MenuItem>
                  <MenuItem value="45">45-Day Old</MenuItem>
                  <MenuItem value="60">60-Day Old</MenuItem>
                  <MenuItem value="90">90-Day Old</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={tempFilters.paymentStatus}
                  onChange={(e) =>
                    handleTempFilterChange("paymentStatus", e.target.value)
                  }
                  label="Payment Status"
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={"RECEIVED"}>Paid</MenuItem>
                  <MenuItem value={"PENDING"}>Unpaid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Apply / Clear buttons */}
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  fullWidth
                  size="small"
                  data-testid="apply-filters-btn"
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  size="small"
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {meeshoData?.orders?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={orders}
              page={page}
              rowsPerPage={rowsPerPage}
              count={meeshoData?.orders?.total || 0}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={meeshoData?.orders?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedOrder?._id) {
              setOrderDetails(selectedOrder);
              setDetailsDialog(true);
            }
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            deleteStageAction(selectedOrder?._id);
          }}
          disabled={!permission.deletePermission}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialog}
        onClose={() => setStatusDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedOrders.length > 1
            ? "Bulk Update Status"
            : "Update Order Status"}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="New Status"
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reason (Optional)"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkStatusUpdate}
            disabled={!newStatus}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent dividers>
          {orderDetails && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Sub Order No</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {orderDetails.subOrderNo}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Order Date</Typography>
                    <Typography variant="body1">
                      {moment.utc(orderDetails?.orderDate).format("DD/MM/YYYY")}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Customer State</Typography>
                    <Typography variant="body1">
                      {orderDetails.customerState}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Status</Typography>
                    {getStatusChip(orderDetails.status)}
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Product</Typography>
                    <Typography variant="body1">
                      {orderDetails.productName}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">SKU</Typography>
                    <Typography variant="body1">{orderDetails.sku}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Size</Typography>
                    <Typography variant="body1">{orderDetails.size}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Quantity</Typography>
                    <Typography variant="body1">
                      {orderDetails.quantity}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Listed Price</Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.supplierListedPrice)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">
                      Discounted Price
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.supplierDiscountedPrice)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Status History */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Status History
                </Typography>
                {orderDetails.statusHistory &&
                orderDetails.statusHistory.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>Changed At</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderDetails.statusHistory
                          .slice()
                          .reverse()
                          .map((h, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{h.status}</TableCell>
                              <TableCell>
                                {moment.utc(h?.changedAt).format("DD/MM/YYYY")}
                              </TableCell>
                              <TableCell>{h.reason || "-"}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No history available
                  </Typography>
                )}
              </Box>

              {/* Payment Sheet Status */}
              {orderDetails.paymentLiveStatus && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Payment Sheet
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Live Status</Typography>
                        <Chip
                          label={
                            orderDetails.paymentLiveStatus === "return"
                              ? "Return"
                              : "Delivered"
                          }
                          color={
                            orderDetails.paymentLiveStatus === "return"
                              ? "warning"
                              : "success"
                          }
                          size="small"
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <DialogConfirm
        isOpen={isDelete}
        onClose={closeDelete}
        title="Are you sure you want to delete the order?"
        onConfirm={handleDeleteOrder}
        btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
      />
    </Box>
  );
};

export default OrderList;
